"use client";

import { useTranslations } from "next-intl";
import { AppInput, AppSelect, AppSelectOption } from "@/components/design/forms";
import type { ParticipantFormData } from "./registrationFlowTypes";
import {
    LEVEL_SELECT_OPTIONS,
    ROLE_SELECT_OPTIONS,
} from "@/lib/participantFieldOptions";
import {
    isSalutationMrSelection,
    isSalutationMsSelection,
} from "@/lib/participantGenderSalutation";

export interface ParticipantPartner2DemographicsGridProps {
    participant: ParticipantFormData;
    index: number;
    errors: Record<string, string>;
    isActive: boolean;
    partner2UiActive: boolean;
    onUpdate: (data: Partial<ParticipantFormData>) => void;
}

export function ParticipantPartner2DemographicsGrid({
    participant,
    index,
    errors,
    isActive,
    partner2UiActive,
    onUpdate,
}: ParticipantPartner2DemographicsGridProps) {
    const t = useTranslations("strings.registrationFlow");
    const tf = useTranslations("strings.participantFieldOptions");
    return (
        <div className="grid min-w-0 w-full max-w-full grid-cols-12 gap-1.5">
                                                {/* Gender Selection */}
                                                <div className="col-span-6 md:col-span-2">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("genderLabel")}
                                                    </label>
                                                    <div className="flex min-h-[30px] flex-wrap items-center gap-x-2 gap-y-1">
                                                        <label className="flex items-center gap-1 cursor-pointer group">
                                                            <div className="relative">
                                                                <AppInput
                                                                    type="radio"
                                                                    name={`partner2-gender-${index}`}
                                                                    checked={isSalutationMrSelection(
                                                                        participant.partner2Gender,
                                                                    )}
                                                                    onChange={() =>
                                                                        onUpdate(
                                                                            {
                                                                                partner2Gender:
                                                                                    "mr",
                                                                            },
                                                                        )
                                                                    }
                                                                    className="sr-only"
                                                                    disabled={
                                                                        !partner2UiActive
                                                                    }
                                                                />
                                                                <div
                                                                    className={`w-3.5 h-3.5 rounded-full border-2 transition-all ${
                                                                        isSalutationMrSelection(
                                                                            participant.partner2Gender,
                                                                        )
                                                                            ? partner2UiActive
                                                                                ? "border-fest-burgundy bg-fest-burgundy"
                                                                                : "border-fest-subtle-text bg-fest-subtle-text"
                                                                            : "border-fest-border-strong bg-white group-hover:border-fest-burgundy"
                                                                    }`}
                                                                >
                                                                    {isSalutationMrSelection(
                                                                        participant.partner2Gender,
                                                                    ) && (
                                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                                            <div className="w-1 h-1 rounded-full bg-white"></div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span
                                                                className={`text-xs font-medium transition-colors ${
                                                                    isSalutationMrSelection(
                                                                        participant.partner2Gender,
                                                                    )
                                                                        ? "text-fest-heading"
                                                                        : "text-fest-subtle-text group-hover:text-fest-heading"
                                                                }`}
                                                            >
                                                                {t("salutationMr")}
                                                            </span>
                                                        </label>
                                                        <label className="flex items-center gap-1 cursor-pointer group">
                                                            <div className="relative">
                                                                <AppInput
                                                                    type="radio"
                                                                    name={`partner2-gender-${index}`}
                                                                    checked={isSalutationMsSelection(
                                                                        participant.partner2Gender,
                                                                    )}
                                                                    onChange={() =>
                                                                        onUpdate(
                                                                            {
                                                                                partner2Gender:
                                                                                    "ms",
                                                                            },
                                                                        )
                                                                    }
                                                                    className="sr-only"
                                                                    disabled={
                                                                        !partner2UiActive
                                                                    }
                                                                />
                                                                <div
                                                                    className={`w-3.5 h-3.5 rounded-full border-2 transition-all ${
                                                                        isSalutationMsSelection(
                                                                            participant.partner2Gender,
                                                                        )
                                                                            ? partner2UiActive
                                                                                ? "border-fest-burgundy bg-fest-burgundy"
                                                                                : "border-fest-subtle-text bg-fest-subtle-text"
                                                                            : "border-fest-border-strong bg-white group-hover:border-fest-burgundy"
                                                                    }`}
                                                                >
                                                                    {isSalutationMsSelection(
                                                                        participant.partner2Gender,
                                                                    ) && (
                                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                                            <div className="w-1 h-1 rounded-full bg-white"></div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span
                                                                className={`text-xs font-medium transition-colors ${
                                                                    isSalutationMsSelection(
                                                                        participant.partner2Gender,
                                                                    )
                                                                        ? "text-fest-heading"
                                                                        : "text-fest-subtle-text group-hover:text-fest-heading"
                                                                }`}
                                                            >
                                                                {t("salutationMs")}
                                                            </span>
                                                        </label>
                                                    </div>
                                                </div>

                                                {/* Age - visible on mobile in same row as gender */}
                                                <div className="col-span-6 md:col-span-1 md:order-6">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("ageLabel")}
                                                    </label>
                                                    <AppSelect
                                                        value={
                                                            participant.partner2Age ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            onUpdate({
                                                                partner2Age:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        }
                                                        disabled={!partner2UiActive}
                                                        className={`w-full px-1 py-1.5 text-sm bg-white border rounded-lg focus:outline-none transition-all appearance-none cursor-pointer ${
                                                            partner2UiActive
                                                                ? "border-fest-border-strong text-fest-heading focus:border-fest-burgundy"
                                                                : "border-fest-border-warm text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        <AppSelectOption value="">
                                                            -
                                                        </AppSelectOption>
                                                        {Array.from(
                                                            { length: 83 },
                                                            (_, i) => 18 + i,
                                                        ).map((age) => (
                                                            <AppSelectOption
                                                                key={age}
                                                                value={age.toString()}
                                                            >
                                                                {age}
                                                            </AppSelectOption>
                                                        ))}
                                                    </AppSelect>
                                                </div>

                                                {/* First Name */}
                                                <div className="col-span-12 md:col-span-2 md:order-2">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("firstNameLabel")}
                                                    </label>
                                                    <AppInput
                                                        type="text"
                                                        value={
                                                            participant.partner2FirstName ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            onUpdate({
                                                                partner2FirstName:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        }
                                                        className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                            partner2UiActive
                                                                ? "border-fest-border-strong text-fest-heading focus:border-fest-gold"
                                                                : "border-fest-border-warm text-fest-subtle-text focus:border-fest-gold"
                                                        }`}
                                                        placeholder={t("placeholderFirstNameFemale")}
                                                        disabled={!partner2UiActive}
                                                    />
                                                    {isActive &&
                                                        errors.partner2FirstName && (
                                                            <p className="text-xs text-fest-red mt-0.5">
                                                                {
                                                                    errors.partner2FirstName
                                                                }
                                                            </p>
                                                        )}
                                                </div>

                                                {/* Last Name */}
                                                <div className="col-span-12 md:col-span-2 md:order-3">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("lastNameLabel")}
                                                    </label>
                                                    <AppInput
                                                        type="text"
                                                        value={
                                                            participant.partner2LastName ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            onUpdate({
                                                                partner2LastName:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        }
                                                        className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                            partner2UiActive
                                                                ? "border-fest-border-strong text-fest-heading focus:border-fest-gold"
                                                                : "border-fest-border-warm text-fest-subtle-text focus:border-fest-gold"
                                                        }`}
                                                        placeholder={t("placeholderLastNameFemale")}
                                                        disabled={!partner2UiActive}
                                                    />
                                                    {isActive &&
                                                        errors.partner2LastName && (
                                                            <p className="text-xs text-fest-red mt-0.5">
                                                                {
                                                                    errors.partner2LastName
                                                                }
                                                            </p>
                                                        )}
                                                </div>

                                                {/* Level */}
                                                <div className="col-span-12 md:col-span-2 md:order-4">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("levelLabel")}
                                                    </label>
                                                    <AppSelect
                                                        value={
                                                            participant.partner2Level ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            onUpdate({
                                                                partner2Level: e
                                                                    .target
                                                                    .value as any,
                                                            })
                                                        }
                                                        disabled={!partner2UiActive}
                                                        className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg focus:outline-none transition-all appearance-none cursor-pointer border-fest-border-strong focus:border-fest-burgundy ${
                                                            !participant.partner2Level
                                                                ? "text-fest-subtle-text"
                                                                : "text-fest-heading"
                                                        }`}
                                                    >
                                                        <AppSelectOption value="">
                                                            {t("selectPlaceholder")}
                                                        </AppSelectOption>
                                                        {LEVEL_SELECT_OPTIONS.map((opt) => (
                                                            <AppSelectOption key={opt.value} value={opt.value}>
                                                                {tf(opt.labelKey)}
                                                            </AppSelectOption>
                                                        ))}
                                                    </AppSelect>
                                                </div>

                                                {/* Role */}
                                                <div className="col-span-12 md:col-span-3 md:order-5">
                                                    <label
                                                        className={`block text-xs font-medium mb-0.5 ${
                                                            partner2UiActive
                                                                ? "text-fest-heading"
                                                                : "text-fest-subtle-text"
                                                        }`}
                                                    >
                                                        {t("tangoRoleLabel")}
                                                    </label>
                                                    <AppSelect
                                                        value={
                                                            participant.partner2Role ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            onUpdate({
                                                                partner2Role: e
                                                                    .target
                                                                    .value as any,
                                                            })
                                                        }
                                                        disabled={!partner2UiActive}
                                                        className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg focus:outline-none transition-all appearance-none cursor-pointer border-fest-border-strong focus:border-fest-burgundy ${
                                                            !participant.partner2Role
                                                                ? "text-fest-subtle-text"
                                                                : "text-fest-heading"
                                                        }`}
                                                    >
                                                        <AppSelectOption value="">
                                                            {t("selectPlaceholder")}
                                                        </AppSelectOption>
                                                        {ROLE_SELECT_OPTIONS.map((opt) => (
                                                            <AppSelectOption key={opt.value} value={opt.value}>
                                                                {tf(opt.labelKey)}
                                                            </AppSelectOption>
                                                        ))}
                                                    </AppSelect>
                                                </div>
                                            </div>
    );
}
